"use client"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import {
  updateAiUseCaseAction,
  updateAiUseCaseGuardrailAction,
  reorderAiUseCasesAction,
  type AiUseCaseView,
} from "@/server-actions/ai-use-cases"
import { aiUseCaseGuardrailQueryKey, aiUseCasesQueryKey } from "@/hooks/queries/ai-use-cases"
import { withQueryHandling } from "@/lib/with-query-handling"

export function useUpdateAiUseCaseMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: {
      id: string
      display_name: string
      description?: string | null
    }): Promise<AiUseCaseView> => {
      const result = await withQueryHandling(updateAiUseCaseAction(input), {
        loading: "Saving use case...",
        success: "Use case saved",
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: aiUseCasesQueryKey })
    },
  })
}

export function useUpdateAiUseCaseGuardrailMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: { id: string; guardrail_prompt: string }): Promise<AiUseCaseView> => {
      const result = await withQueryHandling(updateAiUseCaseGuardrailAction(input), {
        loading: "Saving guardrail...",
        success: "Guardrail saved",
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: async (_data, input) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: aiUseCasesQueryKey }),
        queryClient.invalidateQueries({ queryKey: aiUseCaseGuardrailQueryKey(input.id) }),
      ])
    },
  })
}

export function useReorderAiUseCasesMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (orderedIds: string[]) => {
      const result = await withQueryHandling(
        reorderAiUseCasesAction({ ordered_ids: orderedIds }),
        { loading: "Saving order...", success: "Order saved" },
      )
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: aiUseCasesQueryKey })
    },
  })
}
